import { useMutation, useQuery } from '@apollo/react-hooks';
import React, { useContext, useEffect, useState } from 'react';
import jsPDF from "jspdf";
import html2canvas from "html2canvas"
import styled from 'styled-components';
import toast from 'react-hot-toast';
import ReactTooltip from 'react-tooltip';
import { AppContext } from '../../context/AppContext';
import PDFTemplate from '../PDFTemplateCampaignAgreement';
import {
  ACTIVATE_SMS_CAMPAIGNS,
  GET_CURRENT_ORGANIZATION,
  GET_CURRENT_USER,
  UPDATE_SHORTCODE,
} from '../../graphql/queries';
import colors from '../../styles/colors';
import Button from '../Button';
import Card from '../Card';
import * as phone from '../../util/PhoneNumber';
import Input, { MoneyInput } from '../Input';
import { getErrorMessage } from '../../util/ErrorUtil';
import { IconEnum } from '../Icons';
import Icon from '../Icon';
import AccessLevels from '../../util/AccessLevels';
import moment from 'moment';

export const MessageDetails = styled.div`
  display: flex;
  font-size: 1.2rem;
  color: ${colors.grey3};
  padding: 0 5px;
`;

const HeadingText = styled.h2`
  color: ${colors.lightBlack};
  font-size: 21px;
`;
const Text = styled.p`
  color: ${colors.grey3};
  font-size: 16px;
  line-height: 1.8em;
  margin-bottom: 1.8em;
`;

const Table = styled.table`
  border: 1px solid gainsboro;
  border-radius: 5px;
  margin: 15px 0 15px 0;
  padding: 15px;
`;

const TableRow = styled.tr``;

const TableCell = styled.td`
  color: ${colors.grey3};
  font-size: 16px;
  line-height: 1.8em;
  margin-bottom: 1.8em;
  text-align: left;
`;

const TableHeader = styled.th`
  color: ${colors.lightBlack};
  font-size: 16px;
  line-height: 1.8em;
  margin-bottom: 1.8em;
  padding-right: 20px;
`;

const Form = styled.form`
  width: 80%;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const ShortCodeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const ConfigureCampaigns = () => {
  const { data } = useQuery(GET_CURRENT_ORGANIZATION);
  const { data: currentUser } = useQuery(GET_CURRENT_USER);
  const { app, setApp } = useContext(AppContext);
  const [shortcode, setShortcode] = useState(() => {
    return app.shortcode ? app.shortcode : '';
  });
  const [minMonthlyCharges, setMinMonthlyCharge] = useState(100);
  const [userRole, setUserRole] = useState('');
  const [error, setError] = useState('');

  const [activateCampaigns, { loading }] = useMutation(ACTIVATE_SMS_CAMPAIGNS, {
    onCompleted(data) {
      if (data.activateSmsCampaign.campaignPhoneNumber) {
        const campaignPhoneNumber = data.activateSmsCampaign.campaignPhoneNumber || '';
        const phoneNumber = data.activateSmsCampaign.phoneNumber || campaignPhoneNumber;
        setApp((app) => ({ ...app, phoneNumber, campaignPhoneNumber }));
      }
    },
  });

  const [updateOrgShortCode, { loading: shortCodeUpdating }] = useMutation(
    UPDATE_SHORTCODE,
    {
      onCompleted(data) {
        if (data.updateOrganizationShortcode.shortcode && data?.updateOrganizationShortcode?.minMonthlyCharges>=0) {
          const shortcode = data.updateOrganizationShortcode.shortcode || '';
          const minMonthlyCharges = data.updateOrganizationShortcode.minMonthlyCharges >= 0 ? (data.updateOrganizationShortcode.minMonthlyCharges / 100) : 0;
          setApp((app) => ({ ...app, shortcode }));
          setMinMonthlyCharge(minMonthlyCharges);
          toast.success('Updated Successfully');
        } else if (data.updateOrganizationShortcode.shortcode) {
          const shortcode = data.updateOrganizationShortcode.shortcode || '';
          setApp((app) => ({ ...app, shortcode }));
          toast.success('Updated Successfully');
        }
      },
      onError(error) {
        toast.error(getErrorMessage(error));
      },
      // refetchQueries: [{ query: GET_CURRENT_ORGANIZATION }],
      // awaitRefetchQueries: true,
    },
  );

  useEffect(() => {
    if (data?.getCurrentOrganization) {
      const minMonthlyCharges = data.getCurrentOrganization.minMonthlyCharges ? (data.getCurrentOrganization.minMonthlyCharges / 100) : data.getCurrentOrganization.minMonthlyCharges===0?0: 0;
      setMinMonthlyCharge(minMonthlyCharges);
    }
    if (currentUser.getCurrentUser) {
      const userRole = currentUser.getCurrentUser.currentOrganizationRole;
      setUserRole(userRole);
    }
  }, [data, currentUser]);

  const setCode = (e) => {
    const { value } = e.target;
    const regex = /^[a-zA-Z0-9]{1,20}$/;
    const isValid = regex.test(value);
    setShortcode(value);

    if (isValid) {
      setError('');
    } else {
      setError(
        'Please enter up to 20 characters without any empty spaces or special characters.',
      );
    }
  };

  function downloadCampaignAgreement() {
    const formattedDate = moment().unix();
    const accountName = data.getCurrentOrganization.name;
    const filename = `${accountName}_${formattedDate}.pdf`;

    const input = document.getElementById("agreement-content");

    const options = {
      scale: 2,
      useCORS: true,
    };

    html2canvas(input, options).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      pdf.save(filename);
    });
  }

  return (
    <Form>
      <Card width="100%" padding="20px 30px">
        {app.campaignPhoneNumber ? (
          <>
            <Input
              top
              type="text"
              width="100%"
              header="PHONE NUMBER"
              value={phone.formatPhoneNumber(app.phoneNumber || app.campaignPhoneNumber)}
              readOnly
              margin="0px 0px 15px 0px"
            />

            {userRole === AccessLevels.SuperAdmin ? (
              <MoneyInput
                top
                width="100%"
                type="number"
                header="MINIMUM MONTHLY CHARGE"
                value={minMonthlyCharges}
                onChange={(e) => {
                  const amount = e.target.value;

                  setMinMonthlyCharge(amount);
                }}
                required
              />
            )
              : null}

            <div
              style={{
                display: 'flex',
                padding: '10px 0',
                alignItems: 'center',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center' }}
                data-tip="No spaces or special characters allowed. This value will be included in your campaign URLs to provide branding and click-through tracking."
              >
                <MessageDetails>Short-Code</MessageDetails>{' '}
                <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
              </div>
            </div>
            <ShortCodeContainer>
              <Input
                top
                type="text"
                // header="Short Code"
                onChange={setCode}
                value={app.shortCode || shortcode}
                margin="0px 0px 0px 0px"
                maxLength="20"
              />

              <Button
                width="200px"
                text="Update"
                loading={shortCodeUpdating}
                type="button"
                onClick={() => {
                  if (!error && shortcode.length > 1 && minMonthlyCharges!=="" && minMonthlyCharges >= 0) {
                    const variableObj = { shortcode };
                    if (userRole === AccessLevels.SuperAdmin) {
                      variableObj.minMonthlyCharges = minMonthlyCharges * 100;
                    }
                    updateOrgShortCode({ variables: variableObj });
                  } else if (shortcode.length < 1 && (minMonthlyCharges === '' || Number.isNaN(minMonthlyCharges))) {
                    toast.error("Short-code can't be empty & Minimum monthly charge must be a number greater than or equal to 0");
                  } else if (minMonthlyCharges === '' || Number.isNaN(minMonthlyCharges)) {
                    toast.error("Minimum monthly charge must be a number greater than or equal to 0");
                  } else if (shortcode.length < 1) {
                    toast.error("Short-code can't set to be empty");
                  } else {
                    toast.error('Failed to update. Enter appropriate value for all fields');
                  }
                }}
                margin="0 0 0 20px"
              />
            </ShortCodeContainer>
            <p style={{ color: 'red', fontSize: '13px', padding: '0 0 10px' }}>
              {error}
            </p>
          </>
        ) : null}

        <Text>
          SMS and MMS (if you want to include a photo or longer text) text
          campaigns are the easiest, fastest, and most effective way to get
          mouths to your tables. And it has one of the highest returns on
          investment of any marketing channel!
        </Text>
        <HeadingText>Text recipients</HeadingText>
        <Text>
          Unless you select Tags when creating a campaign, all of your contacts
          will receive text campaigns. Opt-out information is automatically
          attached to each campaign.
        </Text>
        <HeadingText>Pricing</HeadingText>
        <Table>
          <thead>
            <TableRow>
              <TableHeader>Credits per month</TableHeader>
              <TableHeader>Cost per credit</TableHeader>
            </TableRow>
          </thead>
          <tbody>
            <TableRow>
              <TableCell>Up to 3,000</TableCell>
              <TableCell>$0.036</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>3,001-7,500</TableCell>
              <TableCell>$0.024</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>7,501-25,000</TableCell>
              <TableCell>$0.018</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>25,001-50,000</TableCell>
              <TableCell>$0.015</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>At least 50,001</TableCell>
              <TableCell>$0.014</TableCell>
            </TableRow>
          </tbody>
        </Table>
        <Text>
          SMS Campaigns: 1 credit per 160 characters
          <br />
          MMS Campaigns: 3 credits
          <br />
          Your credit card will automatically be charged after you send an
          SMS/MMS campaign and charges will appear on your monthly Check This
          Out invoice.
        </Text>
        {(minMonthlyCharges > 0  && minMonthlyCharges!=null) && (
          <Text>
              {data?.getCurrentOrganization?.name} will be charged a minimum monthly 
              fee of ${minMonthlyCharges} that will be credited towards campaign fees 
              and word-of-mouth engine fees incurred each 30 day period.
          </Text>
        )} 
        <HeadingText>MMS campaigns</HeadingText>
        <Text>
          Please allow 48 hours from account activation for Check This Out to
          enable MMS on your campaign's phone number.
        </Text>
        <HeadingText>Terms and conditions</HeadingText>
        <Text>
          By clicking on the Activate SMS Campaigns button you agree to the
          pricing provided above and verify that you have received express
          written consent to message the contacts in your Check This Out
          account.
          <br />
          All other Check This Out Terms of Use and Privacy Policy is available at <a href="https://checkthisout.io" target="_blank">www.checkthisout.io</a>.
        </Text>
        <>
          {!app.campaignPhoneNumber && (
            <>
              <HeadingText>How to get started</HeadingText>
              <Text>
                Click on the Activate SMS Campaigns button below then click on
                the SMS icon on the left side menu
              </Text>
              <Button
                width="250px"
                text="ACTIVATE SMS CAMPAIGNS"
                loading={loading}
                type="button"
                onClick={activateCampaigns}
                margin="30px 0"
              />
            </>
          )}
        </>

        {app.campaignPhoneNumber ? (
        <>
         <div id="agreement-content" style={{ position: 'absolute', left: '-9999px' }}>
            <div>
              <PDFTemplate
              accountName = { data?.getCurrentOrganization?.name }
              smsOptInAt={ data?.getCurrentOrganization?.smsCampaign?.OptInAt }
              minMonthlyCharges={(minMonthlyCharges && minMonthlyCharges!=null) ? minMonthlyCharges:0}
              />,
            </div>
          </div> 
          <Button
              width="50%"
              text="DOWNLOAD THIS SMS ACTIVATION AGREEMENT"
              type="button"
              onClick={downloadCampaignAgreement}
              margin="30px 0"
          />
        </>
        ):null}
      </Card>
      <ReactTooltip
        place="right"
        type="dark"
        effect="solid"
        clickable={false}
      />
    </Form>
  );
};

export default ConfigureCampaigns;
